const ABRA_EVENT_DELAY = 800

class QuickView extends HHBaseElement {
  static get observedAttributes() {
    return ['product-handle', 'variant-id']
  }

  productHandle = null
  productHTML = ''
  variantId = null
  variants = null
  editLineItemKey = null
  editLineItemQuantity = null
  target = null
  _isReadyPromise = null

  bundleProducts = []
  bundleConfig = null
  currentBundleIndex = 0
  bundleCollectionId = 0
  isBundleMode = false
  bundleSubmitting = false
  bundleSelections = []
  parentVariantId = null
  variantChangeObserver = null
  bundleInterval = null

  constructor() {
    super()
    this.$title = this.querySelector('[slot="title"]')
    this.$content = this.querySelector('[slot="content"]')
    this.slidePanel = this.querySelector('slide-panel')
    this.collectionGrid = document.querySelector('.collection-grid')
    this._handleClick = this._handleClick.bind(this)
    this._updateVariants = this._updateVariants.bind(this)
    this.close = this.close.bind(this)

    this._openSlideout = this._openSlideout.bind(this)
    this._updateVariantInPanel = this._updateVariantInPanel.bind(this)
    this._handleBundleSelection = this._handleBundleSelection.bind(this)
    this._handleVariantChange = this._handleVariantChange.bind(this)
    this._initQuickShopGalleries = this._initQuickShopGalleries.bind(this)
    this._handleSlidePanelOpen = this._handleSlidePanelOpen.bind(this)
    this._handleQuickShopChange = this._handleQuickShopChange.bind(this)
  }

  connectedCallback() {
    this._attachListeners()
    this._setupFocusTrap()
  }

  disconnectedCallback() {
    this._removeListeners()
    this._cleanupBundleObservers()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'product-handle') {
      if (
        oldValue === newValue &&
        this.getAttribute('variant-id') !== this.variantId
      ) {
        this._updateVariantInPanel(this.getAttribute('variant-id'))
        return
      }

      if (!!newValue) {
        this.variantId = this.getAttribute('variant-id')
        this._showProduct(newValue, oldValue)
      } else {
        this.close()
      }
    }
  }

  showProduct(productHandle, variantId) {
    this.setAttribute('variant-id', variantId)
    this.setAttribute('product-handle', productHandle)
  }

  close() {
    this.slidePanel.open = false
    this._reset()

    if (this._lastFocusElement) {
      setTimeout(() => {
        this._lastFocusElement?.focus()
        this._lastFocusElement = null
      }, 100)
    } else {
      document.body.focus()
    }
  }

  async _fetchProduct() {
    const view = this.target || 'quick-shop'

    let currentProductHandle = this.productHandle
    if (this.isBundleMode && this.bundleProducts.length > 0) {
      currentProductHandle = this.bundleProducts[this.currentBundleIndex]
    }

    let bundleParams = ''
    if (this.isBundleMode) {
      bundleParams = `&bundle_index=${this.currentBundleIndex}&bundle_total=${this.bundleProducts.length}`
    }

    const response = await fetch(
      `${__GLOBAL__.routes.rootUrl}/products/${currentProductHandle}?view=${view}${bundleParams}&variant=${this.variantId || ''}`
    )

    if (response.ok) {
      this.productHTML = await response.text()
    } else {
      throw new Error('Failed to fetch product')
    }
  }

  _onProductRenderComplete() {
    try {
      this._refreshLoyaltyLion()
      this._refreshYotpoLoyalty()

      this._updateVariants()
      this._initQuickShopGalleries()

      if (this.isBundleMode) {
        this._setupBundleMode()
      }
    } catch (error) {
      console.error('Error in _onProductRenderComplete:', error)
    }
  }

  async _initQuickShopGalleries() {
    const galleries = this.$content?.querySelectorAll(
      'swiper-with-media.pdp-gallery'
    )
    if (!galleries?.length) return

    const initGalleries = () => {
      galleries.forEach((gallery) => {
        if (gallery.hasAttribute('initialized')) return

        if (typeof gallery.reInitialize === 'function') {
          gallery.reInitialize()
        } else if (typeof gallery.refreshSwiper === 'function') {
          gallery.refreshSwiper()
        }
      })
    }

    try {
      await customElements.whenDefined('swiper-container')
      await customElements.whenDefined('swiper-with-media')

      requestAnimationFrame(() => {
        initGalleries()

        const needsRetry = Array.from(galleries).some(
          (gallery) => !gallery.hasAttribute('initialized')
        )
        if (needsRetry) {
          setTimeout(initGalleries, 100)
        }
      })
    } catch (error) {
      console.error('Error initializing quick shop galleries:', error)
    }
  }

  _updateVariants() {
    if (!this.variants) return
    const variants = this.variants.split(',')

    this.querySelectorAll('[data-variant-id]').forEach((element) => {
      const variantId = element.getAttribute('data-variant-id')
      if (!variants.includes(variantId)) {
        element.parentElement?.remove()
      }
      if (!element.getAttribute('data-variant-id')) return
    })
  }

  _setupBundleMode() {
    this._cleanupBundleObservers()
    this._attachBundleListeners()
    this._setupVariantChangeDetection()
  }

  _setupVariantChangeDetection() {
    this.variantChangeObserver = new MutationObserver(() => {
      this._handleVariantChange()
    })

    const possibleContainers = [
      '.variant-selectors',
      '.product-options',
      'variant-radios',
      'variant-selectors',
      '.variant-wrapper',
      'form[action*="/cart/add"]',
    ]

    let container = null
    for (const selector of possibleContainers) {
      container = this.$content.querySelector(selector)
      if (container) break
    }

    if (container) {
      this.variantChangeObserver.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
      })
    } else {
      const form = this.$content.querySelector('form')
      if (form) {
        this.variantChangeObserver.observe(form, {
          childList: true,
          subtree: true,
          attributes: true,
        })
      }
    }

    this.$content.addEventListener('variant:changed', this._handleVariantChange)

    let lastVariantId = this._getCurrentVariantId()
    this.bundleInterval = setInterval(() => {
      const currentId = this._getCurrentVariantId()
      if (currentId && currentId !== lastVariantId) {
        lastVariantId = currentId
        this._handleVariantChange()
      }
    }, 500)
  }

  _cleanupBundleObservers() {
    if (this.variantChangeObserver) {
      this.variantChangeObserver.disconnect()
      this.variantChangeObserver = null
    }

    if (this.bundleInterval) {
      clearInterval(this.bundleInterval)
      this.bundleInterval = null
    }

    if (this.$content) {
      this.$content.removeEventListener(
        'variant:changed',
        this._handleVariantChange
      )
    }
  }

  _refreshLoyaltyLion() {
    try {
      // refresh data-lion-points-for-product widget for quickshop
      if (window.lion) {
        window.lion.ui.components.buyWithPoints.refresh()
        window.lion.ui.refresh()
      }
    } catch (error) {
      console.error(error)
    }
  }

  _refreshYotpoLoyalty() {
    try {
      // refresh Yotpo PDP Points widget for quickshop
      if (typeof yotpoWidgetsContainer?.initWidgets === 'function') {
        yotpoWidgetsContainer.initWidgets()
      }
    } catch (error) {
      console.error(error)
    }
  }

  get product() {
    const container = document.createElement('div')
    container.innerHTML = this.productHTML
    return container
  }

  get productTitle() {
    return this.product?.querySelector('[data-product-title]').innerHTML || ''
  }

  get productContent() {
    return this.product?.innerHTML || ''
  }

  _attachListeners() {
    document.addEventListener('click', this._handleClick)
    document.addEventListener('quick-shop::close', this.close)

    document.addEventListener('main-product:on-updated', this._updateVariants)
    this.addEventListener('variant:selected', this._handleVariantSelect)

    document.addEventListener('slide-panel-open', this._handleSlidePanelOpen)
    document.addEventListener('click', this._handleQuickShopChange)
  }

  _removeListeners() {
    document.removeEventListener('click', this._handleClick)
    document.removeEventListener('quick-shop::close', this.close)
    document.removeEventListener(
      'main-product:on-updated',
      this._updateVariants
    )
    document.removeEventListener('slide-panel-open', this._handleSlidePanelOpen)
    document.removeEventListener('click', this._handleQuickShopChange)
  }

  // Abra Promotions listens for these to re-render dynamic pricing inside the panel.
  // The delay lets the panel content (and any variant re-render) settle first.
  _handleSlidePanelOpen(e) {
    if (e.detail?.id !== 'quick-shop') return

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('quickshop:open', {
          detail: {
            productHandle: this.productHandle,
            variantId: this.getAttribute('variant-id') || this.variantId,
            isBundleMode: this.isBundleMode,
          },
        })
      )
    }, ABRA_EVENT_DELAY)
  }

  _handleQuickShopChange(e) {
    if (!e.target.closest('quick-shop')) return

    const swatchWrapper = e.target.closest('[data-swatch-input]')
    if (swatchWrapper) {
      const input = swatchWrapper.querySelector('input[data-variant-id]')
      if (!input) return

      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('quickshop:changed', {
            detail: {
              productHandle: this.productHandle,
              type: 'variant',
              variantId: input.dataset.variantId || null,
              variantTitle: input.value || null,
              productId: input.dataset.productId || null,
              filter: null,
            },
          })
        )
      }, ABRA_EVENT_DELAY)
      return
    }

    const label = e.target.closest('label[for]')
    if (!label) return
    const root = label.getRootNode()
    const input =
      (root.getElementById ? root.getElementById(label.htmlFor) : null) ||
      document.getElementById(label.htmlFor)
    if (!input?.matches('input[data-swatch-group]')) return

    window.dispatchEvent(
      new CustomEvent('quickshop:changed', {
        detail: {
          productHandle: this.productHandle,
          type: 'filter',
          variantId: null,
          variantTitle: null,
          productId: null,
          filter: input.dataset.swatchGroup || null,
        },
      })
    )
  }

  _initializeBundleProducts(handle) {
    const productCards = document.querySelectorAll('product-card')
    let bundleData = null

    for (const card of productCards) {
      if (card.getAttribute('product-handle') === handle) {
        const scriptElement = card.querySelector('script[data-bundle-products]')

        if (scriptElement) {
          try {
            bundleData = JSON.parse(scriptElement.textContent)
            break
          } catch (error) {
            console.error('Error parsing bundle products JSON:', error)
          }
        }
      }
    }

    if (
      bundleData &&
      Array.isArray(bundleData.children) &&
      bundleData.children.length
    ) {
      this.bundleProducts = bundleData.children
      this.bundleConfig = bundleData
      this.currentBundleIndex = 0
      this.bundleSelections = []

      if (!this.parentVariantId) {
        this.parentVariantId = this.getAttribute('variant-id')
      }
    } else {
      // Degrade to a normal quick shop — the wizard would otherwise open on the parent
      // bundle product, which has no variants to pick.
      console.error('Bundle products data not found or invalid')
      this.bundleProducts = []
      this.bundleConfig = null
      this.bundleSelections = []
      this.isBundleMode = false
      this.target = 'quick-shop'
    }
  }

  /** Port of snippets/generate-hash.liquid — keep in sync or the app rejects the bundle. */
  _bundleUid(parentVariantId, variantId, priceInCents) {
    const hashMap = 'cP!kLu&NQs'
    const sum =
      Number(parentVariantId) +
      Number(variantId) +
      Number(priceInCents) +
      539482716305

    return String(sum)
      .split('')
      .map((digit) => hashMap[Number(digit)])
      .join('')
  }

  /** Mirror of _getBundlePrice in component-bundle-products.js — keep in sync. */
  _getBundlePrice(components) {
    const total = components.reduce((sum, c) => sum + c.op * c.q, 0) / 100

    const discountType = this.bundleConfig?.discountType
    const discountAmount = parseFloat(this.bundleConfig?.discountAmount)
    let price = 0

    if (
      discountType === 'fixed price' &&
      this.bundleConfig?.fixedDiscountPrice
    ) {
      price = parseFloat(this.bundleConfig.fixedDiscountPrice) / 100
    } else if (discountType === 'dollar amount off total') {
      price = total - Math.floor(discountAmount)
    } else if (discountType === 'percentage off total') {
      price = total - total * (discountAmount / 100)
    } else {
      // No discount rule configured — charge full price rather than billing 0.
      price = total
    }

    return { total, price }
  }

  _handleBundleSelection(e) {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (this.bundleSubmitting) return

    // Price must come from the rendered panel, not the Ajax product JSON: the uid hash is
    // built server-side from the presentment-currency price, which the JSON doesn't give us.
    const selectedVariantId = this._getCurrentVariantId()
    const productForm = this.$content.querySelector('product-form')
    const productId = productForm?.dataset.productId
    const priceInCents = productForm?.dataset.variantPrice

    if (!selectedVariantId || !productId || !priceInCents) {
      console.error(
        'Could not determine variant, product or price - cannot continue with bundle'
      )
      return
    }

    this.bundleSelections.push({
      productHandle: this.bundleProducts[this.currentBundleIndex],
      variantId: selectedVariantId,
      productId,
      priceInCents: Number(priceInCents),
    })

    this.currentBundleIndex++
    this.bundleSubmitting = true

    if (this.currentBundleIndex < this.bundleProducts.length) {
      this._fetchProduct()
        .then(() => {
          this._setInnerHTML(this.$content, this.productContent)
          this._onProductRenderComplete()
        })
        .catch((error) => {
          console.error('Error fetching next product:', error)
        })
        .finally(() => {
          this.bundleSubmitting = false
        })
    } else {
      this._processBundleToCart().finally(() => {
        this.bundleSubmitting = false
      })
    }
  }

  _handleVariantSelect(e) {
    e.preventDefault()
    e.stopImmediatePropagation()
    document.dispatchEvent(
      new CustomEvent('variant:submitted', {
        detail: {
          ...e.detail,
          collectionId: this.bundleCollectionId,
        },
      })
    )
  }

  async _processBundleToCart() {
    const parentId = this.parentVariantId

    if (!parentId) {
      console.error('No parent variant ID available for bundle')
      return
    }

    // q is always 1 — a child with quantity > 1 arrives as repeated selections.
    const components = this.bundleSelections.map((selection) => ({
      v: String(selection.variantId),
      q: 1,
      p: String(selection.productId),
      op: selection.priceInCents,
      uid: this._bundleUid(
        parentId,
        selection.variantId,
        selection.priceInCents
      ),
    }))

    const bundlePrice = this._getBundlePrice(components)

    // One line item for the parent variant, children in properties. Must match
    // component-bundle-products.js and custom-bundle-main.ts, or the cart doesn't
    // recognise this as a bundle and no discount is applied.
    const items = [
      {
        id: parentId,
        quantity: 1,
        properties: {
          _is_bundle: 'true',
          _bundle_items: JSON.stringify({ data: components }),
          _bundle_price: `${bundlePrice.price}`,
          _bundle_original_price: `${bundlePrice.total}`,
        },
      },
    ]

    try {
      if (window.__CART__CONTROLLER__) {
        await window.__CART__CONTROLLER__.add(items, false)
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'))
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        })

        if (!response.ok) {
          throw new Error('Failed to add bundle to cart')
        }

        document.dispatchEvent(new CustomEvent('cart:update'))
      }

      this.close()
    } catch (error) {
      console.error('Error adding bundle to cart:', error)
    }
  }

  async _handleClick(e) {
    if (
      e.target.dataset.productHandle &&
      e.target.classList.contains('js-toggle-quick-shop')
    ) {
      e.preventDefault()
      this._isReadyPromise = new Deferred()
      let button = e.target
      let handle = button.dataset.productHandle
      let variantId = button.dataset.variantId
      this.variants = button.dataset.variants || null
      this.bundleCollectionId = button.dataset.collection || null
      this.target = button.dataset.target || null
      this.editLineItemKey = button.dataset.editLineItemKey || null
      this.editLineItemQuantity = button.dataset.editLineItemQuantity || null

      this.isBundleMode = this.target === 'quick-shop-bundle'

      if (this.isBundleMode) {
        this.parentVariantId = variantId
        this._initializeBundleProducts(handle)
      }

      await this.showProduct(handle, variantId)
      this._isReadyPromise.resolve()
    } else if (this.$title.innerHTML != this.dataset.title) {
      this.$title.innerHTML = this.dataset.title
    }
  }

  _reset() {
    this.productHandle = null
    this.variantId = null
    this.editLineItemKey = null
    this.editLineItemQuantity = null
    this.productHTML = ''
    this.removeAttribute('product-handle')
    this.$content.innerHTML = ''
    this.$title.innerHTML = ''

    this.bundleProducts = []
    this.bundleConfig = null
    this.currentBundleIndex = 0
    this.isBundleMode = false
    this.bundleSubmitting = false
    this.bundleSelections = []
    this.parentVariantId = null

    this._cleanupBundleObservers()
  }

  _setInnerHTML = function (elm, html) {
    try {
      elm.innerHTML = html
      Array.from(elm.querySelectorAll('script')).forEach((oldScript) => {
        const newScript = document.createElement('script')
        Array.from(oldScript.attributes).forEach((attr) =>
          newScript.setAttribute(attr.name, attr.value)
        )
        newScript.appendChild(document.createTextNode(oldScript.innerHTML))
        oldScript.parentNode.replaceChild(newScript, oldScript)
      })
    } catch (error) {
      console.error('Error setting innerHTML:', error)
    }
  }

  async _showProduct(productHandle, oldValue) {
    this._setInnerHTML(this.$content, '')
    this.productHandle = productHandle

    try {
      await this._fetchProduct()
      this._setInnerHTML(this.$content, this.productContent)
      this._openSlideout()

      this._isReadyPromise.resolve()
      this._onProductRenderComplete()

      this._lastFocusElement = document.activeElement
      if (this._firstFocusableElement) {
        setTimeout(() => {
          this._firstFocusableElement.focus()
        }, 100)
      }
    } catch (error) {
      console.error('Failed to load product:', error)
      this._isReadyPromise.reject(new Error('Failed to load product'))
    }
  }

  _openSlideout() {
    this.slidePanel.open = true
  }

  _updateVariantInPanel(newVariantId) {
    const variantDropdown = this.slidePanel.querySelector('variant-dropdown')

    if (variantDropdown) {
      variantDropdown.setAttribute('variant', newVariantId)
      this._openSlideout()
    }
    // this.slidePanel
    //   .querySelector('variant-dropdown')
    //   .setAttribute('variant', newVariantId)

    // this._openSlideout()
  }

  get isReadyPromise() {
    return this._isReadyPromise.promise
  }

  _getCurrentVariantId() {
    try {
      const input = this.$content.querySelector('input[name="id"]')
      if (input && input.value) {
        return input.value
      }

      const variantElement = this.$content.querySelector(
        '[data-selected-variant-id]'
      )
      if (variantElement) {
        return variantElement.getAttribute('data-selected-variant-id')
      }

      const variantDropdown = this.$content.querySelector('variant-dropdown')
      if (variantDropdown) {
        return variantDropdown.getAttribute('variant')
      }

      return this.variantId
    } catch (e) {
      console.error('Error getting current variant ID:', e)
      return null
    }
  }

  _handleVariantChange() {
    if (!this.isBundleMode) return

    setTimeout(() => {
      this._attachBundleListeners()
    }, 100)
  }

  _attachBundleListeners() {
    if (!this.isBundleMode) return

    const productForm = this.$content.querySelector('product-form')
    if (!productForm) return

    const isSelectable = productForm.getAttribute('selectable') === 'true'
    if (!isSelectable) return

    const submitButton = productForm.querySelector('button[type="submit"]')
    if (!submitButton) return

    submitButton.removeEventListener('click', this._handleBundleSelection)
    submitButton.addEventListener('click', this._handleBundleSelection)
  }
}
window.customElements.define('quick-shop', QuickView)
